import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, ReactNode, useContext, useEffect } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { ApiError, api, setCsrfToken } from './api'
import { BugMark } from './components/Brand'
import type { User } from './types'

type AuthValue = {
  user: User | null
  loading: boolean
  /** Set when the session could not be checked at all, as opposed to being signed out. */
  unavailable: ApiError | null
  retry: () => void
  setLogin: (user: User, csrf: string) => void
  /** `notice` is shown on the sign-in page, for sign-outs the user caused on purpose. */
  logout: (notice?: string) => Promise<void>
}

// A server that is restarting says nothing about the session; only 4xx answers do.
function serverUnavailable(error: unknown) {
  return error instanceof ApiError && (error.status === 0 || error.status >= 500)
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient()
  const navigate = useNavigate()
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => api.me(signal),
    retry: (failures, error) =>
      failures < 2 &&
      serverUnavailable(error) &&
      error instanceof ApiError &&
      error.code !== 'offline',
    staleTime: 60_000,
  })

  useEffect(() => {
    if (me.data?.csrf_token) setCsrfToken(me.data.csrf_token)
  }, [me.data])

  useEffect(() => {
    const expired = () => {
      setCsrfToken('')
      client.clear()
      client.setQueryData(['auth', 'me'], null)
      navigate('/login', { replace: true, state: { sessionExpired: true } })
    }
    window.addEventListener('bug:session-expired', expired)
    return () => window.removeEventListener('bug:session-expired', expired)
  }, [client, navigate])

  function setLogin(user: User, csrf: string) {
    setCsrfToken(csrf)
    client.setQueryData(['auth', 'me'], { user, csrf_token: csrf })
  }

  async function logout(notice?: string) {
    try {
      await api.logout()
    } catch {
      // The session may already be gone; signing out locally is what matters.
    } finally {
      setCsrfToken('')
      client.clear()
      client.setQueryData(['auth', 'me'], null)
      navigate('/login', { replace: true, state: notice ? { notice } : undefined })
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user: me.data?.user || null,
        loading: me.isLoading,
        unavailable: !me.data && serverUnavailable(me.error) ? (me.error as ApiError) : null,
        retry: () => void me.refetch(),
        setLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Hooks and their provider intentionally share this small auth boundary.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthProvider is missing')
  return value
}

export function ProtectedRoute() {
  const auth = useAuth()
  const location = useLocation()
  if (auth.loading)
    return (
      <div className="boot-screen">
        <BugMark size={34} />
        <span>Opening your workspace</span>
      </div>
    )
  if (!auth.user && auth.unavailable)
    return (
      <div className="boot-screen" role="alert">
        <BugMark size={34} />
        <span>
          {auth.unavailable.code === 'offline'
            ? 'You are offline. Reconnect to open your workspace.'
            : 'The workspace is not responding. Your session is unchanged.'}
        </span>
        <button className="secondary-button" onClick={auth.retry}>
          Try again
        </button>
      </div>
    )
  if (!auth.user)
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: `${location.pathname}${location.search}` }}
      />
    )
  return <Outlet />
}

export function PlatformAdminRoute() {
  const { user } = useAuth()
  if (user?.platform_role !== 'admin') return <Navigate to="/403" replace />
  return <Outlet />
}
